import React, { useState } from 'react';
import PropTypes from 'prop-types';
// mui
import { Select, MenuItem, Box, Card, CardHeader, CardContent, FormControlLabel, Grid, Stack, Switch } from '@mui/material';
// components
import { isNullOrEmpty } from 'src/utils/checkEmpty';
import EmptyContent from 'src/components/EmptyContent';
import LabelWidget from 'src/components/_dashboard/common/LabelWidget';
import Gallery from './components/Watercraft/Gallery';
import Table from './components/Watercraft/Table';
import LCChart from '../common/LCChart';
import ComingSoonCover from 'src/components/ComingSoonCover';
// ----------------------------------------------------------------------

Watercraft.propTypes = {
  mode: PropTypes.string,
  view: PropTypes.string,
  data: PropTypes.array
};

export const WA_TYPE = [
  { value: 'personal-watercraft-(pwc)-boats', label: 'Personal Watercraft (PWC)', type: 'Personal Watercraft' },

  { value: 'banana-boats', label: 'Banana', type: 'Boats' },
  { value: 'bowrider-boats', label: 'Bowrider', type: 'Boats' },
  { value: 'cabin-cruiser-boats', label: 'Cabin Cruiser', type: 'Boats' },
  { value: 'catamaran-boats', label: 'Catamaran', type: 'Boats' },
  { value: 'centre-console-boats', label: 'Centre Console', type: 'Boats' },
  { value: 'cuddy-cabins-boats', label: 'Cuddy Cabins', type: 'Boats' },
  { value: 'deck-boats', label: 'Deck', type: 'Boats' },
  { value: 'dinghy-boats', label: 'Dinghy', type: 'Boats' },
  { value: 'fishing-boats', label: 'Fishing', type: 'Boats' },
  { value: 'game-boats', label: 'Game', type: 'Boats' },
  { value: 'houseboats', label: 'House', type: 'Boats' },
  { value: 'jet-boats', label: 'Jet', type: 'Boats' },
  { value: 'lifeboats', label: 'Life', type: 'Boats' },
  { value: 'pontoon-boat', label: 'Pontoon', type: 'Boats' },
  { value: 'runabout-boats', label: 'Runabout', type: 'Boats' },
  { value: 'sedan-bridge-boat', label: 'Sedan Bridge', type: 'Boats' },
  { value: 'trawler-boats', label: 'Trawler', type: 'Boats' },
  { value: 'wakeboard/-ski-boats', label: 'Wakeboard/ Ski', type: 'Boats' },

  { value: 'motor-yacht-boats', label: 'Motor Yacht', type: 'Yachts/Mega' },
];

export default function Watercraft({ mode, view = 'show', data = [], chartData }) {
  const [viewAll, setViewAll] = useState(false);

  const getValidData = () => {
    return viewAll ? data : data.filter((item) => item.assetStatus !== 'divested');
  };

  const [selectedChartIndex, setSelectedChartIndex] = useState(0); // 0: Purchase, 1: Market, 2: Estate


  const { vesselTypeCount, vesselTypeMarket, vesselTypeRValue, vesselTypeESValue } = chartData;
  const mergedVesselTypes = WA_TYPE.map((type) => ({
    ...type,
    count: vesselTypeCount[type.value] || 0,
    marketValue: vesselTypeMarket[type.value] || 0,
    rValue: vesselTypeRValue[type.value] || 0,
    esValue: vesselTypeESValue[type.value] || 0,
  }));
  const chartTitles = ['Market Value', 'Replacement Value', 'Estate Value'];
  const typeCountMap = mergedVesselTypes.reduce((acc, curr) => {
    if (!acc[curr.type]) {
      acc[curr.type] = 0;
    }
    acc[curr.type] += curr.count;
    return acc;
  }, {});

  console.log(data);

  // Prepare chart arrays
  const labelCountList = Object.keys(typeCountMap); 
  const dataCountList = Object.values(typeCountMap); 
  const typeValueMap = mergedVesselTypes.reduce((acc, curr) => {
    if (!acc[curr.type]) {
      acc[curr.type] = 0;
    }
    acc[curr.type] += curr.marketValue;
    return acc;
  }, {});
  const typeRValueMap = mergedVesselTypes.reduce((acc, curr) => {
    if (!acc[curr.type]) {
      acc[curr.type] = 0;
    }
    acc[curr.type] += curr.rValue;
    return acc;
  }, {});
  const typeESValueMap = mergedVesselTypes.reduce((acc, curr) => {
    if (!acc[curr.type]) {
      acc[curr.type] = 0;
    }
    acc[curr.type] += curr.esValue;
    return acc;
  }, {});
  const dataList = [
    Object.values(typeValueMap),
    Object.values(typeRValueMap),
    Object.values(typeESValueMap)];

  const renderData = () =>
    mode === 'gallery' ? (
      <Gallery data={data} />
    ) : (
      <Box sx={{ minHeight: 360 }}>
        <FormControlLabel
          sx={{ position: 'absolute', top: '46px', right: '46px', zIndex: 999 }}
          control={<Switch size="small" checked={viewAll} onChange={(event) => setViewAll(event.target.checked)} />}
          label={viewAll ? 'View Active' : 'View All'}
          labelPlacement="start"
        />
        <Table data={getValidData()} />
      </Box>
    );

  return (
    <Stack spacing={2}>
      {view === 'show' && (
        <Grid container>
          <Grid item xs={12} sm={6} md={4}>
            <Stack sx={{ m: 1 }} spacing={1}>
              <Stack sx={{ m: 1, pb: 3, height: 420 }} spacing={1}  >
                <LabelWidget sx={{ height: '33%', justifyContent: 'space-between' }} label="Total Number of Vessels" data={chartData.totalNumberOfVessels} />
                <Grid container sx={{ height: '33%', justifyContent: 'space-between' }}>
                  <LabelWidget
                    data={chartData.totalStatesWithRegister}
                    label="Total States with Registration"
                    sx={{ height: '100%', width: '49%' }}
                  />
                  <LabelWidget
                    data={chartData.totalStatesWithMooring}
                    label="Total States with Mooring"
                    sx={{ height: '100%', width: '49%' }}
                  />
                </Grid>
                <LabelWidget sx={{ height: '33%', justifyContent: 'space-between' }} label="Total Number of Operators" data={chartData.totalNumberOfOperators} />
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ m: 1, display: 'flex', flexDirection: 'column', pb: 3, height: 420 }}>
              {isNullOrEmpty(dataCountList) && (
                <ComingSoonCover noData={true} style={{ marginTop: 25 }} />
              )}
              <CardHeader sx={{
                textAlign: 'center',
                '& .MuiCardHeader-content': {
                  width: '100%' // Make title span full width
                },
                '& .MuiCardHeader-action': {
                  position: 'absolute',
                  right: 12,
                  top: 12
                }
              }} title="Total Fleet Size" />
              <Box sx={{ flex: 1 }}>
                <LCChart
                  type="quantity"
                  label={labelCountList}
                  data={dataCountList}
                />
              </Box>
            </Card>
          </Grid>

          {/* ONE SWITCHABLE COLLECTION VALUE CHART */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                m: 1,
                display: 'flex',
                flexDirection: 'column',
                pb: 3, // ✅ Adds bottom padding
                height: 420
              }}
            >
              <CardHeader
                sx={{
                  textAlign: 'center',
                  '& .MuiCardHeader-content': {
                    width: '100%' // Make title span full width
                  },
                  '& .MuiCardHeader-action': {
                    position: 'absolute',
                    right: 12,
                    top: 12
                  }
                }}
                title={
                  <Box sx={{ pt: 1 }}>Total Watercraft Value</Box>
                }
                action={
                  <Select
                    size="small"
                    value={selectedChartIndex}
                    onChange={(e) => setSelectedChartIndex(Number(e.target.value))}
                    sx={{
                      minWidth: 120,
                      fontSize: 12,
                      '& .MuiSelect-select': {
                        py: 0.5,
                        px: 1.5
                      }
                    }}
                  >
                    {chartTitles.map((title, index) => (
                      <MenuItem key={title} value={index}>
                        {title}
                      </MenuItem>
                    ))}
                  </Select>
                }
              />

              <Box sx={{ flex: 1, pb: 2 }}> {/* ✅ Bottom padding inside chart box */}
                <LCChart
                  label={labelCountList}
                  data={dataList[selectedChartIndex]}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}
      <Card>
        <CardContent>{data.length > 0 ? renderData() : <EmptyContent />}</CardContent>
      </Card>
    </Stack>
  );
}
